import * as React from 'react';
import { CheckOutlined, ClockCircleFilled } from '@ant-design/icons';
import { LifecycleEventType, ReleaseCalendarEventType } from '@/types';
import { ReleaseCalendarLifecycleEventData } from '@/helpers';
import { antGrey, rwColors } from '../styles/globalStyles';
import { dayjs } from '../../extendedDayjs';

export enum ReleaseCalendarDotState {
    FutureExpected = 'futureExpected',
    TodayExpected = 'todayExpected',
    Actual = 'actual',
    RolloutIncrement = 'rolloutIncrement',
    Overdue = 'overdue',
}

const OVERDUE_GRACE_MINUTES = 15;
const DOT_SIZE = 18;
const INNER_DOT_SIZE = 8;
const DOT_OVERLAP_OFFSET = 10;

export const getReleaseCalendarDotState = (
    event: ReleaseCalendarLifecycleEventData,
    now: dayjs.Dayjs,
): ReleaseCalendarDotState => {
    if (event.eventDateType === ReleaseCalendarEventType.ReleaseCalendarEventTypeActualDate) {
        return ReleaseCalendarDotState.Actual;
    }
    if (
        event.eventType === LifecycleEventType.LifecycleEventTypeRolloutIncrement ||
        event.eventType === LifecycleEventType.LifecycleEventTypeRolloutCompletion
    ) {
        return ReleaseCalendarDotState.RolloutIncrement;
    }
    const eventTime = dayjs(event.start);
    if (eventTime.isBefore(now.subtract(OVERDUE_GRACE_MINUTES, 'minutes'))) {
        return ReleaseCalendarDotState.Overdue;
    }
    if (eventTime.isSame(now, 'day')) {
        return ReleaseCalendarDotState.TodayExpected;
    }
    return ReleaseCalendarDotState.FutureExpected;
};

export const ReleaseCalendarDot: React.FC<{
    state: ReleaseCalendarDotState;
    color: string;
    leftFraction: number;
    overlapIndex?: number;
    topOffset?: React.CSSProperties['top'];
}> = ({ state, color, leftFraction, overlapIndex = 0, topOffset }) => {
    if (topOffset == null) {
        return null;
    }

    const overlapOffset = overlapIndex * DOT_OVERLAP_OFFSET;
    const baseStyle: React.CSSProperties = {
        position: 'absolute',
        top: topOffset,
        left: `${leftFraction * 100}%`,
        transform: `translate(calc(-50% + ${overlapOffset}px), -50%)`,
        width: DOT_SIZE,
        height: DOT_SIZE,
        borderRadius: '50%',
        boxSizing: 'border-box',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: overlapIndex,
    };

    const innerDotStyle: React.CSSProperties = {
        width: INNER_DOT_SIZE,
        height: INNER_DOT_SIZE,
        borderRadius: '50%',
    };

    switch (state) {
        case ReleaseCalendarDotState.Actual:
            return (
                <span style={{ ...baseStyle, backgroundColor: color }}>
                    <CheckOutlined style={{ color: antGrey.grey1, fontSize: 10 }} />
                </span>
            );
        case ReleaseCalendarDotState.Overdue:
            return (
                <span style={{ ...baseStyle, backgroundColor: rwColors.statusRed }}>
                    <ClockCircleFilled style={{ color: antGrey.grey1, fontSize: 16 }} />
                </span>
            );
        case ReleaseCalendarDotState.RolloutIncrement:
            return (
                <span
                    style={{
                        ...baseStyle,
                        backgroundColor: antGrey.grey1,
                        border: `1px dashed ${color}`,
                        strokeDasharray: '2 2',
                        opacity: '50%',
                    }}
                >
                    <span style={{ ...innerDotStyle, backgroundColor: color, opacity: '50%' }} />
                </span>
            );
        case ReleaseCalendarDotState.TodayExpected:
            return (
                <span style={{ ...baseStyle, backgroundColor: color }}>
                    <span style={{ ...innerDotStyle, backgroundColor: antGrey.grey1 }} />
                </span>
            );
        default:
            return (
                <span style={{ ...baseStyle, backgroundColor: antGrey.grey1, border: `1.5px solid ${color}` }}>
                    <span style={{ ...innerDotStyle, backgroundColor: color }} />
                </span>
            );
    }
};
